import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiTrendingDown,
  FiTrendingUp,
  FiX,
  FiZap,
} from 'react-icons/fi';

import { Button } from '@/shared/components/ui';
import {
  useSnapshot,
  useTestBeforeDeploy,
  useTestBeforeDeployComparison,
} from '../../hooks/useAgentTraining';
import type {
  TestBeforeDeployComparisonResponse,
  TestComparisonRecommendation,
} from '../../services/agentTrainingApi';
import { getMetricLabel } from '../../utils/metric-labels';

/**
 * State machine do PreDeployTestModal:
 *
 *   choose-type ──▶ testing ──▶ results-safe
 *                              ──▶ results-warning
 *                              ──▶ results-danger
 *                              ──▶ error
 */
type ModalStep =
  | 'choose-type'
  | 'testing'
  | 'results-safe'
  | 'results-warning'
  | 'results-danger'
  | 'error';

interface PreDeployTestModalProps {
  isOpen: boolean;
  agentId: string;
  /** Prompt novo que o cliente quer testar antes de salvar */
  newPrompt: string;
  /** Model override (só quando cliente também mudou o modelo) */
  newModel?: string;
  /**
   * Chamado quando cliente confirma "Salvar e publicar". Recebe o
   * `snapshotId` do teste pra ser usado como `baseSnapshotId` ao salvar
   * o agente (liga a nova versão ao teste pré-deploy).
   *
   * `overrideReason` é enviado automaticamente quando o admin clica
   * "Salvar mesmo assim" em recommendation=warning|danger — vira header
   * `X-Override-Reason` no PATCH e dribla o `DeployGate` quando a
   * regressão excede o threshold de -3% do backend.
   */
  onConfirmSave: (snapshotId: string, overrideReason?: string) => void;
  /** Chamado quando cliente clica "Voltar ao editor" ou cancela */
  onCancel: () => void;
  /**
   * Chamado quando cliente clica "Pular teste e salvar" (atalho do
   * step `choose-type`) ou "Salvar sem teste" (atalho do step `error`)
   */
  onSkipTest: () => void;
}

// METRIC_LABELS centralizado em ../../utils/metric-labels.ts — cobre os
// 11 evaluators do sistema interno (pós-migração Langfuse, 2026-05-11/12).

/**
 * Modal do "Test Before Deploy" (Opção A). Fluxo resumido:
 *
 * 1. Cliente escolhe Quick ou Full
 * 2. Dispara `useTestBeforeDeploy` — pode retornar `fromCache: true`
 *    com snapshot já concluído, ou snapshot pending pra polling
 * 3. Polling via `useSnapshot` até `status='completed'`
 * 4. Quando completa, busca comparison via `useTestBeforeDeployComparison`
 * 5. Mostra resultado em 1 de 4 estados:
 *    - `safe` (verde) → "Salvar e publicar" destacado
 *    - `warning` (amarelo) → "Voltar ao editor" + "Salvar mesmo assim"
 *    - `danger` (vermelho) → igual mas CTA principal é "Voltar"
 *    - `error` → "Tentar novamente" / "Salvar sem teste" / "Cancelar"
 */
export const PreDeployTestModal: React.FC<PreDeployTestModalProps> = ({
  isOpen,
  agentId,
  newPrompt,
  newModel,
  onConfirmSave,
  onCancel,
  onSkipTest,
}) => {
  const [step, setStep] = useState<ModalStep>('choose-type');
  const [scenarioType, setScenarioType] = useState<'quick' | 'full'>('quick');
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const testMutation = useTestBeforeDeploy(agentId);
  const { data: snapshot, error: snapshotError } = useSnapshot(
    snapshotId ?? undefined,
  );
  const {
    data: comparisonData,
    error: comparisonError,
  } = useTestBeforeDeployComparison(
    agentId,
    snapshotId ?? undefined,
    { enabled: !!snapshotId && snapshot?.status === 'completed' },
  );

  // Reset state ao abrir
  useEffect(() => {
    if (isOpen) {
      setStep('choose-type');
      setScenarioType('quick');
      setSnapshotId(null);
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Dispara teste quando cliente confirma tipo
  const handleStartTest = useCallback(async (): Promise<void> => {
    setErrorMessage(null);
    setStep('testing');
    try {
      const result = await testMutation.mutateAsync({
        systemPrompt: newPrompt,
        scenarioType,
        ...(newModel ? { model: newModel } : {}),
      });
      setSnapshotId(result.snapshotId);
      // Se veio do cache, `status='completed'` já. O polling do useSnapshot
      // vai pegar o estado e a comparison vai carregar automaticamente.
    } catch (error) {
      setErrorMessage(
        (error as Error)?.message || 'Erro ao iniciar o teste pré-deploy.',
      );
      setStep('error');
    }
  }, [testMutation, newPrompt, newModel, scenarioType]);

  // Detecta mudança no status do snapshot pra avançar o step
  useEffect(() => {
    if (step !== 'testing' || !snapshot) return;
    if (snapshot.status === 'failed') {
      setErrorMessage(snapshot.error || 'Teste falhou inesperadamente.');
      setStep('error');
      return;
    }
    if (snapshot.status === 'completed' && comparisonData) {
      const rec = comparisonData.comparison?.recommendation ?? 'safe';
      setStep(recommendationToStep(rec));
    }
  }, [step, snapshot, comparisonData]);

  // Erros do polling/comparison
  useEffect(() => {
    if (step !== 'testing') return;
    if (snapshotError) {
      setErrorMessage(snapshotError.message);
      setStep('error');
      return;
    }
    if (comparisonError) {
      setErrorMessage(comparisonError.message);
      setStep('error');
    }
  }, [step, snapshotError, comparisonError]);

  const handleConfirm = useCallback((): void => {
    if (!snapshotId) return;
    // Quando recommendation indica regressão (warning/danger), o backend
    // `DeployGate` bloqueia se delta < -3%. Enviar uma justificativa
    // automática descrevendo a aceitação consciente da regressão
    // (≥5 chars, formato exigido pelo gate).
    if (step === 'results-danger' || step === 'results-warning') {
      const comp = comparisonData?.comparison;
      const deltaPct = comp
        ? (comp.overallScoreDelta * 100).toFixed(1)
        : 'desconhecida';
      const overrideReason = `Admin aceitou regressão de ${deltaPct}% detectada no teste pré-deploy (snapshot ${snapshotId}).`;
      onConfirmSave(snapshotId, overrideReason);
      return;
    }
    onConfirmSave(snapshotId);
  }, [snapshotId, onConfirmSave, step, comparisonData]);

  const modalTitle = useMemo((): string => {
    switch (step) {
      case 'choose-type':
        return 'Testar antes de salvar';
      case 'testing':
        return 'Testando novo prompt...';
      case 'results-safe':
        return 'Tudo certo!';
      case 'results-warning':
        return 'Atenção: regressão detectada';
      case 'results-danger':
        return 'Alerta: regressão grave';
      case 'error':
        return 'Não foi possível testar';
    }
  }, [step]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        data-testid="predeploy-test-modal"
        style={
          {
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            // Tema claro fixo (regra do projeto)
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-card-background': '#ffffff',
            '--theme-card-text': '#334155',
            '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-primary': '#8b5cf6',
            '--theme-button-primary': '#8b5cf6',
            '--theme-button-primary-text': '#ffffff',
            '--theme-button-primary-hover': '#7c3aed',
            '--theme-button-secondary': '#ffffff',
            '--theme-button-secondary-text': '#334155',
            '--theme-button-secondary-hover': 'rgba(0, 0, 0, 0.10)',
            '--theme-button-ghost-hover': '#f1f5f9',
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <h2 className="text-lg font-semibold text-slate-800">{modalTitle}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-5 flex-1">
          {step === 'choose-type' && (
            <ChooseTypeStep
              scenarioType={scenarioType}
              onChangeType={setScenarioType}
              onStart={handleStartTest}
              onSkip={onSkipTest}
              isPending={testMutation.isPending}
            />
          )}

          {step === 'testing' && (
            <TestingStep snapshot={snapshot} />
          )}

          {(step === 'results-safe' ||
            step === 'results-warning' ||
            step === 'results-danger') &&
            comparisonData && (
              <ResultsStep
                step={step}
                comparison={comparisonData}
                onConfirmSave={handleConfirm}
                onBackToEdit={onCancel}
              />
            )}

          {step === 'error' && (
            <ErrorStep
              message={errorMessage}
              onRetry={() => setStep('choose-type')}
              onSkipAndSave={onSkipTest}
              onCancel={onCancel}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Helper ──────────────────────────────────────────────────────────────

function recommendationToStep(
  rec: TestComparisonRecommendation,
): 'results-safe' | 'results-warning' | 'results-danger' {
  if (rec === 'danger') return 'results-danger';
  if (rec === 'warning') return 'results-warning';
  return 'results-safe';
}

// ─── Sub-components ──────────────────────────────────────────────────────

const ChooseTypeStep: React.FC<{
  scenarioType: 'quick' | 'full';
  onChangeType: (type: 'quick' | 'full') => void;
  onStart: () => void;
  onSkip: () => void;
  isPending: boolean;
}> = ({ scenarioType, onChangeType, onStart, onSkip, isPending }) => {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 leading-relaxed">
        Antes de publicar o novo prompt, vamos rodá-lo contra alguns cenários
        de teste e comparar com a versão atual. Isso permite detectar se a
        mudança quebrou algum comportamento do agente.
      </p>

      <div className="space-y-2">
        <TypeOption
          selected={scenarioType === 'quick'}
          onClick={() => onChangeType('quick')}
          title="Teste rápido"
          subtitle="5 cenários · ~30 segundos · mais barato"
          recommended
        />
        <TypeOption
          selected={scenarioType === 'full'}
          onClick={() => onChangeType('full')}
          title="Teste completo"
          subtitle="12 cenários · ~2 minutos · mais confiável"
        />
      </div>

      <div
        className="rounded-md px-3 py-2 text-xs"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.06)',
          color: '#6b7280',
        }}
      >
        💡 Se você já testou este mesmo prompt nos últimos 10 minutos, o
        resultado é instantâneo (sem consumir créditos).
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isPending}
          data-testid="predeploy-skip-test"
        >
          Pular teste e salvar
        </Button>
        <Button
          variant="primary"
          onClick={onStart}
          disabled={isPending}
          loading={isPending}
          data-testid="predeploy-start-test"
        >
          Iniciar teste
        </Button>
      </div>
    </div>
  );
};

const TypeOption: React.FC<{
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  recommended?: boolean;
}> = ({ selected, onClick, title, subtitle, recommended }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left rounded-lg border p-3 transition-colors"
    style={{
      backgroundColor: selected ? 'rgba(139, 92, 246, 0.06)' : '#ffffff',
      borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.10)',
    }}
  >
    <div className="flex items-start gap-3">
      <div
        className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{
          borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.25)',
        }}
      >
        {selected && (
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: '#8b5cf6' }}
          />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {recommended && (
            <span
              className="text-xs px-1.5 py-0.5 rounded font-medium"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                color: '#6d28d9',
              }}
            >
              Recomendado
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      </div>
    </div>
  </button>
);

const TestingStep: React.FC<{
  snapshot: ReturnType<typeof useSnapshot>['data'];
}> = ({ snapshot }) => {
  const progress = snapshot?.progress;
  const pct =
    progress && progress.totalScenarios > 0
      ? (progress.completedScenarios / progress.totalScenarios) * 100
      : 0;

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-center">
        <FiZap
          className="h-10 w-10 animate-pulse"
          style={{ color: '#8b5cf6' }}
        />
      </div>

      <div className="text-center">
        <div className="text-sm font-semibold text-slate-800 mb-1">
          {progress?.currentStepLabel ?? 'Preparando teste...'}
        </div>
        <div className="text-xs text-slate-500">
          {progress
            ? `${progress.completedScenarios} de ${progress.totalScenarios} cenários`
            : 'Aguardando...'}
        </div>
      </div>

      <div
        className="rounded-full h-2 overflow-hidden"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}
      >
        <div
          className="h-full transition-all duration-300"
          style={{
            backgroundColor: '#8b5cf6',
            width: `${pct}%`,
          }}
        />
      </div>

      <p className="text-xs text-center text-slate-500">
        Este teste não afeta a produção. O agente responde a clientes
        simulados em sandbox.
      </p>
    </div>
  );
};

const ResultsStep: React.FC<{
  step: 'results-safe' | 'results-warning' | 'results-danger';
  comparison: TestBeforeDeployComparisonResponse;
  onConfirmSave: () => void;
  onBackToEdit: () => void;
}> = ({ step, comparison, onConfirmSave, onBackToEdit }) => {
  const { comparison: comp, snapshot, goldenSignal } = comparison;

  // Sem baseline — mostra resultado cru
  if (!comp) {
    return (
      <div className="space-y-4">
        <div
          className="rounded-lg border p-4"
          style={{
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
            borderColor: 'rgba(59, 130, 246, 0.20)',
          }}
        >
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Primeira vez que este agente é testado
          </div>
          <p className="text-xs text-slate-600">
            Não há treino anterior pra comparar. O score do teste é{' '}
            <strong>
              {snapshot.summary
                ? `${Math.round(snapshot.summary.overallScore * 100)}%`
                : '—'}
            </strong>
            . Isso vira sua baseline — treinos futuros vão comparar com este.
          </p>
        </div>

        {/* Golden cases — sinal complementar de respostas reais */}
        {goldenSignal && (
          <div
            data-testid="predeploy-golden-signal"
            className="mt-6 p-4 rounded-lg"
            style={{
              backgroundColor:
                goldenSignal.passRate >= 0.8 ? '#f0fdf4' : '#fef3c7',
              border: '1px solid',
              borderColor:
                goldenSignal.passRate >= 0.8 ? '#86efac' : '#fcd34d',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-sm font-semibold"
                style={{ color: '#1e293b' }}
              >
                Golden cases (respostas reais)
              </h3>
              <span
                className="text-sm font-bold"
                style={{
                  color:
                    goldenSignal.passRate >= 0.8 ? '#16a34a' : '#d97706',
                }}
              >
                {goldenSignal.passedCases}/{goldenSignal.totalCases} passaram
              </span>
            </div>
            <div
              className="grid grid-cols-2 gap-2 text-xs"
              style={{ color: '#475569' }}
            >
              <div>
                Similaridade média:{' '}
                <strong>
                  {(goldenSignal.averageSimilarity * 100).toFixed(1)}%
                </strong>
              </div>
              <div>
                Threshold:{' '}
                <strong>{(goldenSignal.threshold * 100).toFixed(0)}%</strong>
              </div>
            </div>
            {goldenSignal.totalCases === 0 && (
              <p className="text-xs mt-2" style={{ color: '#64748b' }}>
                Nenhuma conversa ainda foi marcada como EXCELENTE para este
                agente. Marque conversas no chat para gerar este sinal.
              </p>
            )}
            {goldenSignal.passRate < 0.8 && goldenSignal.totalCases > 0 && (
              <p className="text-xs mt-2" style={{ color: '#92400e' }}>
                ⚠️ Este prompt diverge de respostas que você classificou como
                excelentes. Revise os detalhes ou publique mesmo assim.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onBackToEdit}
            data-testid="predeploy-back-to-edit"
          >
            Voltar ao editor
          </Button>
          <Button
            variant="primary"
            onClick={onConfirmSave}
            data-testid="predeploy-confirm-save"
          >
            Salvar e publicar
          </Button>
        </div>
      </div>
    );
  }

  // Com baseline
  const isSafe = step === 'results-safe';
  const isWarning = step === 'results-warning';
  const isDanger = step === 'results-danger';

  const accentColor = isSafe ? '#10b981' : isWarning ? '#f59e0b' : '#dc2626';
  const bgColor = isSafe
    ? 'rgba(16, 185, 129, 0.05)'
    : isWarning
      ? 'rgba(245, 158, 11, 0.05)'
      : 'rgba(239, 68, 68, 0.05)';
  const borderColor = isSafe
    ? 'rgba(16, 185, 129, 0.20)'
    : isWarning
      ? 'rgba(245, 158, 11, 0.20)'
      : 'rgba(239, 68, 68, 0.25)';

  const Icon = isSafe ? FiCheckCircle : isDanger ? FiTrendingDown : FiAlertTriangle;
  const iconLabel = isSafe
    ? 'O novo prompt está tão bom ou melhor que o atual'
    : isWarning
      ? 'Detectamos uma pequena regressão'
      : 'O novo prompt está significativamente pior que o atual';

  const deltaPct = (comp.overallScoreDelta * 100).toFixed(1);
  const deltaSign = comp.overallScoreDelta >= 0 ? '+' : '';

  return (
    <div className="space-y-4">
      {/* Cabeçalho colorido */}
      <div
        className="rounded-lg border p-4"
        style={{ backgroundColor: bgColor, borderColor }}
      >
        <div className="flex items-start gap-3">
          <Icon
            className="h-5 w-5 flex-shrink-0 mt-0.5"
            style={{ color: accentColor }}
          />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800 mb-1">
              {iconLabel}
            </div>
            <div className="text-xs text-slate-600">
              Score geral: {(comp.previousOverallScore * 100).toFixed(0)}% →{' '}
              <strong style={{ color: accentColor }}>
                {(comp.currentOverallScore * 100).toFixed(0)}%
              </strong>{' '}
              ({deltaSign}
              {deltaPct}%)
            </div>
          </div>
        </div>
      </div>

      {/* Evaluators que melhoraram */}
      {comp.improvedEvaluators.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <FiTrendingUp className="h-3 w-3" style={{ color: '#10b981' }} />
            Métricas que melhoraram
          </div>
          <div className="flex flex-wrap gap-1.5">
            {comp.improvedEvaluators.map((ev) => (
              <span
                key={ev.metric}
                className="text-xs px-2 py-0.5 rounded-md font-medium"
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#047857',
                }}
              >
                {getMetricLabel(ev.metric)}:{' '}
                {(ev.from * 100).toFixed(0)}% →{' '}
                <strong>{(ev.to * 100).toFixed(0)}%</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Evaluators que pioraram */}
      {comp.regressedEvaluators.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <FiTrendingDown className="h-3 w-3" style={{ color: '#dc2626' }} />
            Métricas que pioraram
          </div>
          <div className="flex flex-wrap gap-1.5">
            {comp.regressedEvaluators.map((ev) => (
              <span
                key={ev.metric}
                className="text-xs px-2 py-0.5 rounded-md font-medium"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#991b1b',
                }}
              >
                {getMetricLabel(ev.metric)}:{' '}
                {(ev.from * 100).toFixed(0)}% →{' '}
                <strong>{(ev.to * 100).toFixed(0)}%</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Golden cases — sinal complementar de respostas reais */}
      {goldenSignal && (
        <div
          data-testid="predeploy-golden-signal"
          className="mt-6 p-4 rounded-lg"
          style={{
            backgroundColor:
              goldenSignal.passRate >= 0.8 ? '#f0fdf4' : '#fef3c7',
            border: '1px solid',
            borderColor:
              goldenSignal.passRate >= 0.8 ? '#86efac' : '#fcd34d',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: '#1e293b' }}>
              Golden cases (respostas reais)
            </h3>
            <span
              className="text-sm font-bold"
              style={{
                color:
                  goldenSignal.passRate >= 0.8 ? '#16a34a' : '#d97706',
              }}
            >
              {goldenSignal.passedCases}/{goldenSignal.totalCases} passaram
            </span>
          </div>
          <div
            className="grid grid-cols-2 gap-2 text-xs"
            style={{ color: '#475569' }}
          >
            <div>
              Similaridade média:{' '}
              <strong>
                {(goldenSignal.averageSimilarity * 100).toFixed(1)}%
              </strong>
            </div>
            <div>
              Threshold:{' '}
              <strong>{(goldenSignal.threshold * 100).toFixed(0)}%</strong>
            </div>
          </div>
          {goldenSignal.totalCases === 0 && (
            <p className="text-xs mt-2" style={{ color: '#64748b' }}>
              Nenhuma conversa ainda foi marcada como EXCELENTE para este
              agente. Marque conversas no chat para gerar este sinal.
            </p>
          )}
          {goldenSignal.passRate < 0.8 && goldenSignal.totalCases > 0 && (
            <p className="text-xs mt-2" style={{ color: '#92400e' }}>
              ⚠️ Este prompt diverge de respostas que você classificou como
              excelentes. Revise os detalhes ou publique mesmo assim.
            </p>
          )}
        </div>
      )}

      {/* CTAs — ordem muda conforme severidade (F71: não-primário = secondary) */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        {isSafe ? (
          <>
            <Button
              variant="secondary"
              onClick={onBackToEdit}
              data-testid="predeploy-back-to-edit"
            >
              Voltar ao editor
            </Button>
            <Button
              variant="success"
              onClick={onConfirmSave}
              data-testid="predeploy-confirm-save"
            >
              Salvar e publicar
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onConfirmSave}
              data-testid="predeploy-confirm-save"
            >
              Salvar mesmo assim
            </Button>
            <Button
              variant={isDanger ? 'error' : 'warning'}
              onClick={onBackToEdit}
              data-testid="predeploy-back-to-edit"
            >
              Voltar ao editor
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const ErrorStep: React.FC<{
  message: string | null;
  onRetry: () => void;
  onSkipAndSave: () => void;
  onCancel: () => void;
}> = ({ message, onRetry, onSkipAndSave, onCancel }) => (
  <div className="space-y-4">
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: '#fef2f2',
        borderColor: '#fecaca',
      }}
    >
      <div className="flex items-start gap-3">
        <FiAlertTriangle
          className="h-5 w-5 flex-shrink-0 mt-0.5"
          style={{ color: '#dc2626' }}
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Não conseguimos concluir o teste
          </div>
          <div className="text-xs text-slate-600">
            {message ??
              'Houve um erro inesperado ao rodar os cenários. Você pode tentar novamente ou salvar sem teste.'}
          </div>
        </div>
      </div>
    </div>

    <div className="flex items-center justify-end gap-3 pt-2">
      <Button
        variant="secondary"
        onClick={onCancel}
        data-testid="predeploy-error-cancel"
      >
        Cancelar
      </Button>
      <Button
        variant="ghost"
        onClick={onSkipAndSave}
        data-testid="predeploy-error-skip"
      >
        Salvar sem teste
      </Button>
      <Button
        variant="primary"
        onClick={onRetry}
        data-testid="predeploy-error-retry"
      >
        Tentar novamente
      </Button>
    </div>
  </div>
);
